import { useState } from 'react';
import { extractCall, queryClient, useBoard } from '../../lib/data';
import { env } from '../../lib/env';
import { fromDT, toDT } from '../../lib/format';
import { supabase } from '../../lib/supabase';
import { CloseX, useDrawer, useToast } from '../../ui/kit';

// A call that Read.ai did not send on its own (until the webhook lands in V2,
// that is every call): title, time, lead, and optionally the pasted text for
// an AI draft. Spec: docs/specs/call-extraction.md.
export function NewCallDrawer() {
  const { open, close } = useDrawer();
  const toast = useToast();
  const board = useBoard();
  const active = (board.data ?? []).filter(
    (l) => !['won', 'lost', 'disq', 'nurture'].includes(l.stage),
  );
  const [f, setF] = useState({
    title: '',
    when: toDT(new Date().toISOString()),
    minutes: '',
    lead: '',
    source: '',
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set =
    (k: keyof typeof f) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setF({ ...f, [k]: e.target.value });

  async function submit() {
    const startedAt = fromDT(f.when);
    if (!f.title.trim() || !startedAt) {
      setErr('Add a title and when the call happened.');
      return;
    }
    const minutes = f.minutes.trim() ? Number(f.minutes) : null;
    if (minutes !== null && (!Number.isInteger(minutes) || minutes < 0 || minutes > 600)) {
      setErr('Duration is a whole number of minutes.');
      return;
    }
    setBusy(true);
    setErr('');
    const { data: id, error } = await supabase.rpc('create_manual_call', {
      p_title: f.title.trim(),
      p_started_at: startedAt,
      p_duration_min: minutes,
      p_lead: f.lead || null,
    });
    if (error || !id) {
      setBusy(false);
      setErr(error?.message ?? 'The call could not be added.');
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ['calls'] });

    let note = 'Call added.';
    if (env.aiExtraction && f.source.trim().length >= 40) {
      try {
        const r = await extractCall(id as string, f.source);
        note = `Call added, AI drafted ${r.promises} promise${r.promises === 1 ? '' : 's'}.`;
      } catch (e) {
        note = `Call added. ${e instanceof Error ? e.message : String(e)}`;
      }
    }
    setBusy(false);
    toast(note);
    open({ type: 'call', id: id as string });
  }

  return (
    <>
      <div className="dhead">
        <div>
          <h2 id="dtitle">Add call</h2>
          <div className="sm">For a call Read.ai did not send. You review it right after.</div>
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        <div className="panel grid2">
          <label className="field full">
            <span>Title</span>
            <input
              type="text"
              value={f.title}
              onChange={set('title')}
              placeholder="Ouradent × Kurt Dental"
            />
          </label>
          <label className="field">
            <span>When</span>
            <input type="datetime-local" value={f.when} onChange={set('when')} />
          </label>
          <label className="field">
            <span>Minutes</span>
            <input type="number" min={0} max={600} value={f.minutes} onChange={set('minutes')} />
          </label>
          <label className="field full">
            <span>Lead</span>
            <select aria-label="Lead" value={f.lead} onChange={set('lead')}>
              <option value="">Link later</option>
              {active.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.clinic} ({l.person})
                </option>
              ))}
            </select>
          </label>
          {env.aiExtraction && (
            <label className="field full">
              <span>Read.ai summary or transcript (optional)</span>
              <textarea
                rows={8}
                value={f.source}
                onChange={set('source')}
                placeholder="Paste it here and AI drafts the promises, next step and recap."
              />
            </label>
          )}
        </div>
      </div>
      <div className="dfoot">
        {err && (
          <p className="err" role="alert">
            {err}
          </p>
        )}
        <button className="btn primary big" onClick={() => void submit()} disabled={busy}>
          {busy ? 'Adding…' : 'Add call'}
        </button>
        <button className="btn ghost" onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
